import { useEffect, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { NativeSelect, NumberInput, Stack, Text, Textarea } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";

import { ClearableYesNoRadioField } from "@/components/ClearableYesNoRadioField";
import { CONTRACTIONS_DATE_TIME_LINK_ID } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/constants/contractions-link-ids";
import type { ContractionsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/contractions/types/contractions-form";
import { valueSetContainsToNativeSelectOptions } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import { LaborFlowsheetDateTimeFields } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetDateTimeFields";
import { LaborFlowsheetSignatureInitialsField } from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/LaborFlowsheetSignatureInitialsField";
import { QuiltedHealthValueSets } from "@/fhir/enums/quilted-health";
import { questionnaireFieldTestId } from "@/intake-forms/utils/questionnaire-field-testid";
import { getValueSetOptions } from "@/utils/medplum";

interface ContractionsFormFieldsProps {
  form: UseFormReturnType<ContractionsFlowsheetFormValues>;
  clientId: string;
  mode?: "quick-add" | "edit";
  isDrawerOpened?: boolean;
}

export function ContractionsFormFields({
  form,
  clientId,
  mode = "quick-add",
  isDrawerOpened = true,
}: ContractionsFormFieldsProps) {
  const medplum = useMedplum();
  const [strengthOptions, setStrengthOptions] = useState<ValueSetExpansionContains[]>([]);
  const strengthInputProps = form.getInputProps("strength");

  useEffect(() => {
    if (!isDrawerOpened) {
      return;
    }

    let isCancelled = false;

    const loadStrengthOptions = async () => {
      const strengthValueSet = await getValueSetOptions(
        medplum,
        QuiltedHealthValueSets.ContractionsStrength,
      );
      if (isCancelled) {
        return;
      }
      setStrengthOptions(strengthValueSet ?? []);
    };

    void loadStrengthOptions();

    return () => {
      isCancelled = true;
    };
  }, [isDrawerOpened, medplum]);

  return (
    <Stack gap="lg">
      <LaborFlowsheetDateTimeFields
        form={form}
        dateFieldName="contractionDate"
        timeFieldName="contractionTime"
        autoPopulateDateTime={mode === "quick-add"}
        dateTimeGroupLinkId={CONTRACTIONS_DATE_TIME_LINK_ID}
      />

      <NumberInput
        label="Frequency"
        clampBehavior="strict"
        rightSectionWidth={80}
        data-path="contractionsFrequency"
        rightSection={
          <Text pr="xs" size="sm">
            Minutes
          </Text>
        }
        {...form.getInputProps("frequency")}
        data-testid={questionnaireFieldTestId("contractionsFrequency")}
      />

      <NumberInput
        label="Duration"
        clampBehavior="strict"
        rightSectionWidth={80}
        data-path="contractionsDuration"
        rightSection={
          <Text pr="xs" size="sm">
            Seconds
          </Text>
        }
        {...form.getInputProps("duration")}
        data-testid={questionnaireFieldTestId("contractionsDuration")}
      />

      <NativeSelect
        label="Strength"
        data-path="contractionStrength"
        data={valueSetContainsToNativeSelectOptions(strengthOptions)}
        {...strengthInputProps}
        value={strengthInputProps.value ?? ""}
        data-testid={questionnaireFieldTestId("contractionStrength")}
      />

      <ClearableYesNoRadioField
        form={form}
        name="coping"
        label="Coping"
        dataPath="contractionsCoping"
        dataTestId={questionnaireFieldTestId("contractionsCoping")}
      />

      <Textarea
        label="Notes"
        data-path="contractionsNotes"
        {...form.getInputProps("notes")}
        data-testid={questionnaireFieldTestId("contractionsNotes")}
      />

      <LaborFlowsheetSignatureInitialsField
        form={form}
        clientId={clientId}
        label="Sign Initials"
        name="signatureInitials"
        autoPopulateSignature={mode === "quick-add"}
      />
    </Stack>
  );
}
